import { useCallback, useEffect, useState } from 'react';
import {
  ComponentRendering,
  Field,
  Image,
  ImageField,
  LinkField,
  RichTextField,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';
import { sanitize } from 'isomorphic-dompurify';

import {
  ButtonVariantType,
  ComponentProps,
  DynamicHeading,
  getContrastThemeClass,
  resolveColorProperty,
  useBreakpoint,
  withTheme,
} from '@gn-audio/jss-components';
import {
  getAccount,
  getTitleFromAccount,
  getWebStoreApiClient,
  isLoggedIn,
} from '@gn-audio/jss-core';

import { Eyebrow } from 'common/components/Eyebrow';
import NavButton from 'composables/shared/NavButton/NavButton';

type BusinessClubBannerSize = 'small' | 'medium' | 'large';
type logoPosition = 'first' | 'last';
type ButtonVisibilityValue = 'loggedIn' | 'notLoggedIn';

type ButtonVisibility = {
  Value: {
    value: ButtonVisibilityValue;
  };
};

type BusinessClubBannerFields = {
  eyebrow: Field<string>;
  titleLoggedIn: Field<string>;
  titleNotLoggedIn: Field<string>;
  detailsLoggedIn: Field<string>;
  detailsNotLoggedIn: Field<string>;
  size: Field<BusinessClubBannerSize>;
  backgroundColor: Field<string>;
  backgroundImage: ImageField;
  logo: ImageField;
  logoPosition: Field<logoPosition>;
  firstButtonText: Field<string>;
  firstButtonAriaLabel?: Field<string>;
  firstButtonLoginEvent: Field<boolean>;
  firstButtonLink: LinkField;
  firstButtonIcon?: Field<string>;
  firstButtonVariant: Field<ButtonVariantType>;
  firstButtonVisibility: Array<ButtonVisibility>;
  secondButtonText: Field<string>;
  secondButtonAriaLabel?: Field<string>;
  secondButtonLoginEvent: Field<boolean>;
  secondButtonLink: LinkField;
  secondButtonIcon?: Field<string>;
  secondButtonVariant: Field<ButtonVariantType>;
  secondButtonVisibility: Array<ButtonVisibility>;
  isMainHeading: Field<boolean>;
  badgeContent: RichTextField;
  badgeBackgroundColor: Field<string>;
};
type BusinessClubBannerProps = ComponentProps & {
  fields: BusinessClubBannerFields;
};

const imageSizes: { [key in BusinessClubBannerSize]: { h: number; w: number } } = {
  small: {
    h: 420,
    w: 708,
  },
  medium: {
    h: 640,
    w: 708,
  },
  large: {
    h: 900,
    w: 920,
  },
};

type classNamesBySize = Partial<{ [key in BusinessClubBannerSize]: string }>;

const buttonContainerClassNames: classNamesBySize = {
  small: 'pt-8',
  medium: 'pt-8',
  large: 'pt-10',
};

const shouldShowButton = (
  visibilitySettings: Array<ButtonVisibility> | undefined,
  isUserLoggedIn: boolean
): boolean => {
  if (!visibilitySettings?.length) {
    return true;
  }

  const visibilityValues = visibilitySettings.map((item) => item.Value.value);

  if (
    (isUserLoggedIn && visibilityValues.includes('loggedIn')) ||
    (!isUserLoggedIn && visibilityValues.includes('notLoggedIn')) ||
    (visibilityValues.includes('loggedIn') && visibilityValues.includes('notLoggedIn'))
  ) {
    return true;
  }

  return false;
};

const BusinessClubBanner = ({ fields }: BusinessClubBannerProps) => {
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
  const [userFullname, setUserFullname] = useState<string | null>(null);
  const { isBelowMd } = useBreakpoint('md', true);
  const size = fields.size.value;
  const buttonSize = size !== 'small' || isBelowMd ? 'l' : 'm';
  const {
    eyebrow,
    titleLoggedIn,
    titleNotLoggedIn,
    detailsLoggedIn,
    detailsNotLoggedIn,
    firstButtonText,
    firstButtonAriaLabel,
    firstButtonLoginEvent,
    firstButtonLink,
    firstButtonIcon,
    firstButtonVariant,
    firstButtonVisibility,
    secondButtonLink,
    secondButtonLoginEvent,
    secondButtonText,
    secondButtonAriaLabel,
    secondButtonIcon,
    secondButtonVariant,
    secondButtonVisibility,
    backgroundColor,
    backgroundImage,
    logo,
    logoPosition,
    isMainHeading,
    badgeContent,
    badgeBackgroundColor,
  } = fields;
  const isFirstButtonVisible = shouldShowButton(firstButtonVisibility, isUserLoggedIn);
  const isSecondButtonVisible = shouldShowButton(secondButtonVisibility, isUserLoggedIn);
  const bannerBgColorHex = resolveColorProperty(backgroundColor?.value);
  const badgeBgColorHex = resolveColorProperty(badgeBackgroundColor?.value || 'yellow');
  const isBadgeBgBright = getContrastThemeClass(badgeBgColorHex);
  const isBannerBgBright = bannerBgColorHex ? getContrastThemeClass(bannerBgColorHex) : null;

  const parseWelcomeMessage = useCallback((template: string, name: string | null): string => {
    if (!name) return template;
    return template.replace(/\{\{name\}\}/g, name);
  }, []);

  const logInHandler = useCallback(async () => {
    try {
      const webStoreApiClient = getWebStoreApiClient();
      const account = webStoreApiClient.account;

      if (!account) {
        await webStoreApiClient.signIn();
      }
    } catch (error) {
      console.error('Error during login:', error);
    }
  }, []);

  useEffect(() => {
    if (isUserLoggedIn) {
      const account = getAccount();

      if (account) {
        const initials = getTitleFromAccount(account);
        setUserFullname(initials);
      }
    }
  }, [isUserLoggedIn]);

  useEffect(() => {
    const checkIfUserIsLoggedIn = async () => {
      try {
        const loggedIn = await isLoggedIn();

        setIsUserLoggedIn(loggedIn);
      } catch (error) {
        console.error('Error checking user login status:', error);
      }
    };

    checkIfUserIsLoggedIn();
  }, []);

  const titleContent = isUserLoggedIn
    ? parseWelcomeMessage(titleLoggedIn?.value || '', userFullname)
    : titleNotLoggedIn?.value || '';

  const detailsContent = isUserLoggedIn
    ? detailsLoggedIn?.value || ''
    : detailsNotLoggedIn?.value || '';

  return (
    <div
      data-testid="business-club-banner"
      className={classNames('bg-theme relative flex justify-end', isBannerBgBright)}
      {...(bannerBgColorHex && { style: { backgroundColor: bannerBgColorHex } })}
    >
      <div className="left-0 right-0 z-20 m-auto flex h-full w-full max-xl:container-fluid xl:container max-xl:flex-col xl:absolute xl:items-center">
        {backgroundImage?.value && (
          <div className="container-left flex-shrink-0 py-0 xl:hidden">
            <Image
              field={{
                src: backgroundImage?.value?.src,
                alt: backgroundImage?.value?.alt,
              }}
              className="h-[unset] max-h-[550px] w-full rounded-l-full object-cover max-sm:max-h-[400px]"
              height={imageSizes[size]?.h}
              width={imageSizes[size]?.w}
              data-testid="mobile-background-image"
            />
          </div>
        )}
        <div className="relative basis-1/2 pb-16 pt-8 max-xl:container md:pr-6">
          <div>
            {badgeContent?.value && (
              <div
                data-testid="badge"
                className={classNames(
                  'absolute -top-10 right-4 flex aspect-square flex-col items-center justify-center overflow-hidden rounded-full p-4 text-center text-sm xl:-right-14 xl:top-0',
                  isBadgeBgBright
                )}
                style={{
                  backgroundColor: badgeBgColorHex,
                }}
                dangerouslySetInnerHTML={{ __html: badgeContent.value }}
              />
            )}
            {logoPosition?.value !== 'last' && logo?.value?.src && (
              <Image
                field={{
                  src: logo.value?.src,
                  alt: logo.value?.alt,
                }}
                className="mb-6 max-h-14 w-auto"
                width="100%"
                data-testid="logo-first"
              />
            )}
            <div>
              {eyebrow?.value && <Eyebrow className="mb-5" text={eyebrow.value} />}

              {(titleLoggedIn?.value || titleNotLoggedIn?.value) && (
                <DynamicHeading
                  className="heading-1"
                  isMainHeading={isMainHeading?.value || false}
                  data-testid="title"
                  htmlContent={sanitize(titleContent)}
                />
              )}
              {(detailsLoggedIn?.value || detailsNotLoggedIn?.value) && (
                <div
                  className="paragraph-large"
                  data-testid="details"
                  dangerouslySetInnerHTML={{
                    __html: sanitize(detailsContent),
                  }}
                />
              )}
            </div>
            {logoPosition?.value === 'last' && logo?.value?.src && (
              <Image
                field={{
                  src: logo.value?.src,
                  alt: logo.value?.alt,
                }}
                className="mt-6 max-h-14 w-auto"
                width="100%"
                data-testid="logo-last"
              />
            )}
            <div
              className={classNames(buttonContainerClassNames[size], 'flex flex-wrap gap-6')}
              data-testid="button-container"
            >
              {isFirstButtonVisible &&
                firstButtonText?.value &&
                (firstButtonLoginEvent?.value || firstButtonLink?.value?.href) && (
                  <NavButton
                    data-testid="first-button"
                    variant={firstButtonVariant?.value || 'primary'}
                    size={buttonSize}
                    title={firstButtonText?.value}
                    ariaLabel={firstButtonAriaLabel?.value}
                    icon={firstButtonIcon?.value}
                    {...(firstButtonLoginEvent?.value
                      ? { onClick: logInHandler }
                      : { link: firstButtonLink })}
                  />
                )}
              {isSecondButtonVisible &&
                secondButtonText?.value &&
                (secondButtonLoginEvent?.value || secondButtonLink?.value?.href) && (
                  <NavButton
                    data-testid="second-button"
                    variant={secondButtonVariant?.value || 'secondary'}
                    size={buttonSize}
                    title={secondButtonText?.value}
                    ariaLabel={secondButtonAriaLabel?.value}
                    icon={secondButtonIcon?.value}
                    {...(secondButtonLoginEvent?.value
                      ? { onClick: logInHandler }
                      : { link: secondButtonLink })}
                  />
                )}
            </div>
          </div>
        </div>
      </div>
      {backgroundImage?.value && (
        <div style={{ height: `${imageSizes[size]?.h}px` }} className="basis-1/2 max-xl:hidden">
          <Image
            field={{
              src: backgroundImage?.value?.src,
              alt: backgroundImage?.value?.alt,
            }}
            className="h-full rounded-l-full object-cover"
            height={imageSizes[size]?.h}
            width="100%"
            data-testid="desktop-background-image"
          />
        </div>
      )}
    </div>
  );
};

export const getStaticProps = async (rendering: ComponentRendering<BusinessClubBannerFields>) => {
  try {
    const { fields: dataSource, params = {} } = rendering;

    if (!dataSource) {
      throw new Error('No fields found for Business Club Banner');
    }

    const fields = {
      ...dataSource,
      badgeContent: {
        ...dataSource.badgeContent,
        value: sanitize((dataSource.badgeContent?.value as string) || ''),
      },
    };

    return {
      fields,
      params,
    };
  } catch (error) {
    console.error('Error in BusinessClubBanner getStaticProps:', error);
    const { params = {} } = rendering;
    return {
      params,
      fields: {},
    };
  }
};

export default withTheme({ legacyBackgroundFieldName: 'backgroundColor' })(
  withDatasourceCheck()(BusinessClubBanner)
);
